import { useAuth } from "../lib/useAuth";
import { Link } from "react-router-dom";
import { useGetStrutture, useGetPrenotazioni } from "@/lib/supabaseQueries";
import { mobileMenuStore } from "@/lib/mobileMenu";
import {
  Home,
  Users,
  CalendarDays,
  AlertTriangle,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Wrench,
  LogIn,
  LogOut,
  CircleDollarSign,
  Percent,
  Menu,
} from "lucide-react";

const PRIORITA_COLOR: Record<string, { color: string; bg: string }> = {
  "Urgente": { color: "#ef4444", bg: "#fef2f2" },
  "Alta":    { color: "#d97706", bg: "#fef3c7" },
  "Media":   { color: "#5580d6", bg: "#e4eeff" },
  "Bassa":   { color: "#3aaa85", bg: "#e0f5f0" },
};

function KpiCard({ label, value, sub, icon: Icon, iconBg, iconColor, href }: {
  label: string; value: string; sub?: string; icon: any;
  iconBg: string; iconColor: string; href?: string;
}) {
  const inner = (
    <div className="bg-card rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: iconBg }}>
          <Icon className="h-4 w-4" style={{ color: iconColor }} />
        </div>
        {href && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40" />}
      </div>
      <div className="text-2xl font-bold text-foreground leading-none mb-1">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="text-xs text-primary mt-0.5 font-medium">{sub}</div>}
    </div>
  );
  return href ? <Link to={href} className="block">{inner}</Link> : <div>{inner}</div>;
}

export default function Dashboard() {
  const { currentUser } = useAuth();
  const { data: strutture = [] } = useGetStrutture();
  const { data: prenotazioni = [], isLoading } = useGetPrenotazioni();

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const in7days = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const dateStr = today.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const hour = today.getHours();
  const greeting = hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";
  const name = (currentUser?.user_metadata?.full_name as string)?.split(" ")[0] || currentUser?.email?.split("@")[0] || "";
  const inSoggiorno = prenotazioni.filter(p => (p.data_checkin || p.check_in || "") <= todayStr && (p.data_checkout || p.check_out || "") >= todayStr);
  const checkInOggi  = prenotazioni.filter(p => (p.data_checkin || p.check_in || "") === todayStr);
  const checkOutOggi = prenotazioni.filter(p => (p.data_checkout || p.check_out || "") === todayStr);
  const prossimi     = prenotazioni.filter(p => { const d = (p.data_checkin || p.check_in || ""); return d && d > todayStr && d <= in7days; });

  const piattaformaEmoji: Record<string, string> = {
    "Airbnb": "🏠", "Booking.com": "📘", "Vrbo": "🏡", "Diretto": "⭐",
  };

  return (
    <div>
      {/* Mobile floating menu button */}
      <button
        onClick={() => mobileMenuStore.open()}
        className="md:hidden fixed right-4 bottom-6 z-20 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-transform active:scale-95"
        style={{ background: "#1a1a2e" }}
      >
        <Menu className="h-5 w-5 text-white" />
      </button>

      {/* Greeting */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-foreground">{greeting}, {name} 👋</h1>
        <p className="text-sm text-muted-foreground mt-0.5 capitalize">{dateStr}</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Strutture attive"  value={String(strutture.length)}     icon={Home}             iconBg="#fdeedd" iconColor="#c87045" href="/strutture/lista" />
        <KpiCard label="In soggiorno ora"  value={String(inSoggiorno.length)}             icon={Users}            iconBg="#e0f5f0" iconColor="#3aaa85" href="/prenotazioni/lista" />
        <KpiCard label="Ricavi mese"     value={`€ 0`} icon={CircleDollarSign} iconBg="#f0fdf4" iconColor="#22c55e" href="/finanze/panoramica" />
        <KpiCard label="Tasso occupazione" value={`—`}      icon={Percent}          iconBg="#f5f3ff" iconColor="#7c3aed" />
      </div>

      {/* Oggi */}
      {(checkInOggi.length > 0 || checkOutOggi.length > 0) && (
        <div className="bg-card rounded-2xl shadow-sm mb-4 overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="font-semibold text-foreground text-sm">Oggi</h2>
          </div>
          <div className="divide-y divide-border">
            {checkInOggi.map((p: any) => (
              <div key={`in-${p.id}`} className="flex items-center gap-3 px-5 py-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-50 flex-shrink-0">
                  <LogIn className="h-3.5 w-3.5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground">{p.ospite_nome}</span>
                  <span className="text-xs text-muted-foreground ml-2">{p.struttura_nome}</span>
                </div>
                <span className="text-xs text-green-600 font-medium">Check-in</span>
                <span className="text-xs">{piattaformaEmoji[p.piattaforma] ?? "📋"}</span>
              </div>
            ))}
            {checkOutOggi.map((p: any) => (
              <div key={`out-${p.id}`} className="flex items-center gap-3 px-5 py-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-50 flex-shrink-0">
                  <LogOut className="h-3.5 w-3.5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground">{p.ospite_nome}</span>
                  <span className="text-xs text-muted-foreground ml-2">{p.struttura_nome}</span>
                </div>
                <span className="text-xs text-amber-600 font-medium">Check-out</span>
                <span className="text-xs">{piattaformaEmoji[p.piattaforma] ?? "📋"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* In soggiorno + Prossimi arrivi — 2 col su desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* In soggiorno */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-semibold text-foreground text-sm">In soggiorno ora</h2>
            <Link to="/prenotazioni/lista" className="text-xs font-medium flex items-center gap-1 text-primary">
              Tutte <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {inSoggiorno.length === 0 ? (
            <div className="px-5 py-6 text-sm text-muted-foreground">Nessun ospite in soggiorno.</div>
          ) : (
            <div className="divide-y divide-border">
              {inSoggiorno.map((p: any) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3.5">
                  <div>
                    <div className="text-sm font-medium text-foreground">{p.ospite_nome}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.struttura_nome} · out {p.data_checkout}</div>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Prossimi arrivi */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-semibold text-foreground text-sm">Prossimi arrivi</h2>
            <Link to="/prenotazioni/calendario" className="text-xs font-medium flex items-center gap-1" style={{ color: "#5580d6" }}>
              Calendario <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {prossimi.length === 0 ? (
            <div className="px-5 py-6 text-sm text-muted-foreground">Nessun arrivo nei prossimi 7 giorni.</div>
          ) : (
            <div className="divide-y divide-border">
              {prossimi.map((p: any) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3.5">
                  <div>
                    <div className="text-sm font-medium text-foreground">{p.ospite_nome}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.struttura_nome}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-medium text-foreground">{p.data_checkin}</div>
                    <div className="text-xs text-muted-foreground">{piattaformaEmoji[p.piattaforma] ?? ""} {p.piattaforma}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manutenzione + Azioni rapide — 2 col su desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Manutenzione aperta */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-semibold text-foreground text-sm">Manutenzione aperta</h2>
            <Link to="/team/manutenzione" className="text-xs font-medium flex items-center gap-1 text-primary">
              Tutti <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="px-5 py-6 text-sm text-muted-foreground">Nessuna manutenzione aperta.</div>
        </div>

        {/* Azioni rapide */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="font-semibold text-foreground text-sm">Azioni rapide</h2>
          </div>
          <div className="grid grid-cols-2 divide-x divide-y divide-border">
            {[
              { label: "Prenotazioni",    href: "/prenotazioni/lista",      icon: CalendarDays,     iconBg: "#e4eeff", iconColor: "#5580d6" },
              { label: "Calendario",      href: "/prenotazioni/calendario", icon: CalendarDays,     iconBg: "#fdeedd", iconColor: "#c87045" },
              { label: "Finanze",         href: "/finanze/panoramica",      icon: TrendingUp,       iconBg: "#f0fdf4", iconColor: "#22c55e" },
              { label: "Segnalazioni",    href: "/operativo/segnalazioni",  icon: AlertTriangle,    iconBg: "#fff8e0", iconColor: "#d4a017" },
            ].map((a) => (
              <Link key={a.href} to={a.href}
                className="flex flex-col items-center justify-center gap-2 py-5 hover:bg-background transition-colors">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: a.iconBg }}>
                  <a.icon className="h-4 w-4" style={{ color: a.iconColor }} />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{a.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
